import {styled, Box, Skeleton, Stack} from '@mui/material';
import React from 'react';
import DefaultVoteIcon from '../../assets/default-vote-icon.svg';
import useSubmitVote from '../../hooks/useSubmitVote';
import {VoteEnum} from '../../types';
import {OutlinedButton} from './OutlinedButton';
import {TalkingPointDescription} from './TalkingPointDescription';
import {TalkingPointHeading} from './TalkingPointHeading';
import {TalkingPointLabel} from './TalkingPointLabel';
import TalkingPointPopup from './TalkingPointPopup';
import VoteIcon from './VoteIcon';
import VoteLabel from './VoteLabel';
import editIcon from '../../assets/edit-icon.png';
import {getVoteType} from '../../utils/misc';

type CurrentTalkingPointProps = {
  data?: {
    id: string;
    title: string;
    description: string;
    vote: string;
  };
  onClick?: () => void;
  skeleton?: boolean;
  PopupProps?: {
    hideBackdrop: boolean;
    paperCoords: {
      left: number;
      width: number;
    };
  };
};

const Container = styled('div')(({theme}) => ({
  background: 'rgba(255, 255, 255, 0.94)',
  backdropFilter: 'blur(5px)',
  padding: theme.spacing(2),
  border: '1px solid #FFFFFF41',
  borderRadius: theme.typography.pxToRem(16),
}));

const Button = styled(OutlinedButton)(({theme}) => ({
  borderRadius: theme.typography.pxToRem(16),
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(16)}`,
  width: 'fit-content',
}));

function CurrentTalkingPoint({data, skeleton, PopupProps}: CurrentTalkingPointProps) {
  const [open, setOpen] = React.useState(false);
  const vote = useSubmitVote();

  const onSubmit = React.useCallback(
    (tpId: string, type: VoteEnum) => {
      vote(tpId, type);
      setOpen(false);
    },
    [vote, setOpen]
  );

  const onClick = () => {
    setOpen(true);
  };
  return (
    <Container>
      <TalkingPointLabel sx={{mb: 2}}>Current talking point</TalkingPointLabel>

      <TalkingPointHeading>
        {!skeleton && (
          <>
            {data?.id}. {data?.title}
          </>
        )}
        {skeleton && <Skeleton variant="text" width="30%" />}
      </TalkingPointHeading>

      <TalkingPointDescription>
        {!skeleton && <>{data?.description}</>}
        {skeleton && <Skeleton variant="text" />}
      </TalkingPointDescription>
      {(skeleton || data?.vote === '') && (
        <OutlinedButton
          disabled={skeleton}
          sx={{maxWidth: '50%', ml: 'auto', display: 'block'}}
          onClick={onClick}
        >
          <Box component="img" src={DefaultVoteIcon} alt="default vote" />
        </OutlinedButton>
      )}
      {!skeleton && data && data.vote !== '' && (
        <Stack direction="row" justifyContent="space-between">
          <VoteLabel type={getVoteType(data.vote)}>
            <VoteIcon type={getVoteType(data.vote)} /> {data.vote}
          </VoteLabel>
          <Button onClick={onClick}>
            Change vote{' '}
            <Box
              component="img"
              src={editIcon}
              alt="edit"
              sx={{width: '12px', height: '12px', objectFit: 'contain', ml: 2}}
            />
          </Button>
        </Stack>
      )}
      {data && (
        <TalkingPointPopup
          open={open}
          hideBackdrop={!!PopupProps?.hideBackdrop}
          paperCoords={PopupProps?.paperCoords || {left: 0, width: 0}}
          onClose={() => setOpen(false)}
          data={data}
          onSubmit={onSubmit}
        />
      )}
    </Container>
  );
}

export default CurrentTalkingPoint;
